import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 走行環境削除API
 */
export const useDeleteEnvironmentAPI = (): {
  state: APIState;
  deleteEnvironment: () => Promise<boolean>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const deleteEnvironmentAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
    ): Promise<Result<boolean, AxiosResponse>> => {
      try {
        await fmsAPI.delete(`/${projectId}/environments/${environmentId}`);
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  /**
   * @return {Boolean}
   */
  const deleteEnvironment = useAtomCallback(
    useCallback(
      async (get) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) && isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.delete_environment', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return false;
        }
        setState('loading');
        const res = await deleteEnvironmentAPI(
          project!.id,
          environment!.environment_id,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.delete_environment', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          return false;
        }
        notifySuccess({
          message: t('api.fms.delete_environment', {
            status: 'successfully',
          }),
        });
        setState('hasValue');
        return true;
      },
      [notifyError, notifySuccess, deleteEnvironmentAPI, t, getErrorMessage],
    ),
  );

  return {
    state,
    deleteEnvironment,
  };
};
